import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  checkPermission,
  getUserPermissions,
  hasPermission as checkHasPermission,
  hasAnyPermission as checkHasAnyPermission,
  hasAllPermissions as checkHasAllPermissions,
  checkHierarchicalPermission,
  getRolePermissions,
  getUserEffectivePermissions,
  getAllPermissions,
  getRoleStoragePermissions
} from '../services/permissions/permissionService';
import { Permission, PermissionCheckResult, HierarchicalPermission, RolePermissions, UserEffectivePermissions, StoragePermissions } from '../types/permission.types';
import { UserRole } from '../types/user.types';
import { canWriteToPathSync, canReadFromPathSync } from '../services/storage/storagePermissionChecker';

interface PermissionContextType {
  permissions: Permission[];
  loading: boolean;
  hasPermission: (permissionId: string) => boolean;
  hasPermissions: (permissionIds: string[]) => boolean;
  hasAnyPermission: (permissionIds: string[]) => boolean;
  checkPermissionAsync: (permissionId: string) => Promise<PermissionCheckResult>;
  refreshPermissions: () => Promise<void>;
  hasHierarchicalPermissions: boolean;
  effectivePermissions: string[]; // All effective permission IDs
  hasCustomPermissions: boolean; // Whether user has custom permissions
  // Storage permissions
  storagePermissions: StoragePermissions | null;
  canUploadTo: (path: string) => boolean;
  canDownloadFrom: (path: string) => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Custom hooks for common permission checks
export const useHasPermission = (permissionId: string): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(permissionId);
};

export const useHasAnyPermission = (permissionIds: string[]): boolean => {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissionIds);
};

export const useHasAllPermissions = (permissionIds: string[]): boolean => {
  const { hasPermissions } = usePermissions();
  return hasPermissions(permissionIds);
};

export const useDashboardPermissions = () => {
  const { permissions } = usePermissions();
  return permissions.filter(p => p.categoryId === 'dashboard');
};

export const useMenuPermissions = () => {
  const { permissions } = usePermissions();
  return permissions.filter(p => p.categoryId === 'menu');
};

interface PermissionProviderProps {
  children: React.ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { userProfile } = useAuth();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [hierarchicalPermissions, setHierarchicalPermissions] = useState<HierarchicalPermission | null>(null);
  const [effectivePermissions, setEffectivePermissions] = useState<string[]>([]);
  const [hasCustomPermissions, setHasCustomPermissions] = useState(false);
  const [storagePermissions, setStoragePermissions] = useState<StoragePermissions | null>(null);
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    if (!userProfile?.role) {
      setPermissions([]);
      setHierarchicalPermissions(null);
      setEffectivePermissions([]);
      setHasCustomPermissions(false);
      setStoragePermissions(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Load storage permissions for the role
      const storagePerms = await getRoleStoragePermissions(userProfile.role as UserRole);
      setStoragePermissions(storagePerms);

      // Check if user has custom permissions
      if (userProfile.hasCustomPermissions) {
        // Load effective permissions (merged role + custom)
        const effectivePerms = await getUserEffectivePermissions(userProfile);
        const allPerms = await getAllPermissions();

        // Filter permissions based on effective permission IDs
        const userPermissions = allPerms.filter(p =>
          effectivePerms.effectivePermissions.includes(p.id)
        );

        setPermissions(userPermissions);
        setEffectivePermissions(effectivePerms.effectivePermissions);
        setHierarchicalPermissions(effectivePerms.hierarchicalPermissions);
        setHasCustomPermissions(true);

        // Use custom storage permissions if available
        if (effectivePerms.storagePermissions) {
          setStoragePermissions(effectivePerms.storagePermissions);
        }
      } else {
        // Load regular role-based permissions only
        const [userPermissions, rolePerms] = await Promise.all([
          getUserPermissions(userProfile.role as UserRole),
          getRolePermissions(userProfile.role as UserRole)
        ]);

        setPermissions(userPermissions);
        setEffectivePermissions(userPermissions.map(p => p.id));
        setHierarchicalPermissions(rolePerms?.hierarchicalPermissions || null);
        setHasCustomPermissions(false);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions([]);
      setHierarchicalPermissions(null);
      setEffectivePermissions([]);
      setHasCustomPermissions(false);
      setStoragePermissions(null);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.role, userProfile?.hasCustomPermissions]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const hasPermission = useCallback((permissionId: string): boolean => {
    if (!userProfile?.role) return false;

    // Super users always have all permissions
    if (userProfile.role === UserRole.PROPCLOUDAI_SUPER_USER) {
      return true;
    }

    // "Report an Issue" card — universally available during the 2026 consultant
    // CRM audit window. Bypasses the hierarchical permissions system entirely
    // because audit feedback should never be blocked by Roles & Permissions
    // configuration (which most accounts won't have this card configured in).
    // See docs/audit/consultant-crm-audit-plan.md.
    if (permissionId === 'dashboardCards.feedback') {
      return true;
    }

    // Consultation Insights (Jaco 2026-10-26): the weekly sales-meeting page
    // is for everyone, whatever a role's saved Roles & Permissions say.
    if (permissionId === 'dashboardCards.consultation_insights') {
      return true;
    }

    // Check if it's a hierarchical permission
    if (permissionId.startsWith('dashboardCards.')) {
      // If hierarchical permissions are loaded and configured
      if (hierarchicalPermissions && hierarchicalPermissions.dashboardCards) {
        // Parse the permission path (e.g., "dashboardCards.hq")
        const parts = permissionId.split('.');

        if (parts.length >= 2) {
          const cardId = parts[1];
          const card = hierarchicalPermissions.dashboardCards[cardId];

          // Check if the card exists and is enabled
          if (card && card.enabled) {
            // If just checking card access, return true
            if (parts.length === 2) {
              return true;
            }

            // Check menu groups if specified
            if (parts.length >= 4 && parts[2] === 'menuGroups') {
              const groupId = parts.slice(3, parts.indexOf('subItems') > -1 ? parts.indexOf('subItems') : parts.length).join('.');
              const group = card.menuGroups[groupId];

              if (!group?.enabled) {
                return false;
              }

              // Check sub-items if specified
              const subItemsIndex = parts.indexOf('subItems');
              if (subItemsIndex > -1 && parts.length > subItemsIndex + 1) {
                const itemId = parts.slice(subItemsIndex + 1).join('.');
                return group.subItems[itemId] === true;
              }

              return true;
            }
          }
        }

        // Before returning false, check if this is a card that should have role-based fallback
        // This handles cases where hierarchical permissions exist but don't include all cards yet
        if (permissionId === 'dashboardCards.user_impersonation') {
          const impersonationRoles = ['propcloudai_super_user', 'hq_manager'];
          if (impersonationRoles.includes(userProfile.role)) {
            return true;
          }
        }

        // My Cindy Orbit — the consultant's OWN leads only (server enforces
        // contact ownership; Jaco 2026-09-10). Role-based fallback so the new
        // card works without a rolePermissions backfill (absent key = denied
        // otherwise); the Roles & Permissions editor can still switch it off
        // per role once the key exists in stored configs.
        if (permissionId === 'dashboardCards.my_cindy_orbit') {
          const orbitRoles = ['hq_consultant_lvl1', 'hq_independent_consultant_lvl2'];
          if (orbitRoles.includes(userProfile.role)) {
            return true;
          }
        }

        // Capital Investment (Jaco 2026-10-12): role fallback so the card needs no
        // rolePermissions backfill; useCanAccessCapitalInvestment then narrows it
        // to the configured capital consultant (+ super users).
        // Both consultant levels (Jaco 2026-10-22: Pieter moved to Level 1 for
        // his commission and lost the card).
        if (permissionId === 'dashboardCards.capital_investment') {
          if (userProfile.role === 'hq_consultant_lvl1' || userProfile.role === 'hq_independent_consultant_lvl2') {
            return true;
          }
        }

        // Bonds Bonus Dashboard — PERSONAL view, only the bonds consultant sees it.
        // Admins who need to view Liezl's exact dashboard should impersonate;
        // otherwise they use the Bonds Bonus Admin card for the audit/payment flow.
        if (permissionId === 'dashboardCards.bonds_bonus_dashboard') {
          if (userProfile.role === 'hq_bonds') {
            return true;
          }
        }

        // Outstanding Bonds dashboard — HQ management (the callable checks the same roles)
        if (permissionId === 'dashboardCards.outstanding_bonds') {
          const allowed = ['propcloudai_super_user', 'hq_manager', 'hq_admin'];
          if (allowed.includes(userProfile.role)) {
            return true;
          }
        }

        // Bonds Bonus Admin — accountants + super user only
        if (permissionId === 'dashboardCards.bonds_bonus_admin') {
          const allowed = ['propcloudai_super_user', 'hq_manager'];
          if (allowed.includes(userProfile.role)) {
            return true;
          }
        }

        // Consultant Monthly Review (admin dashboard) — HQ Management
        if (permissionId === 'dashboardCards.consultant_reviews') {
          const allowed = ['propcloudai_super_user', 'hq_manager', 'hq_admin'];
          if (allowed.includes(userProfile.role)) {
            return true;
          }
        }

        // Deposit Waiver Report — Super User + HQ Manager + HQ Admin
        if (permissionId === 'dashboardCards.deposit_waiver_report') {
          const allowed = ['propcloudai_super_user', 'hq_manager', 'hq_admin'];
          if (allowed.includes(userProfile.role)) {
            return true;
          }
        }

        // Consultant Monthly Review (consultant-facing) — LVL1 + LVL2 only
        if (permissionId === 'dashboardCards.my_monthly_review') {
          const allowed = ['hq_consultant_lvl1', 'hq_independent_consultant_lvl2'];
          if (allowed.includes(userProfile.role)) {
            return true;
          }
        }

        // Investment Proposals (consultant-facing) — sales roles + HQ Management
        if (permissionId === 'dashboardCards.investmentProposals') {
          const allowed = [
            'propcloudai_super_user',
            'hq_manager',
            'hq_admin',
            'hq_consultant_lvl1',
            'hq_independent_consultant_lvl2',
          ];
          if (allowed.includes(userProfile.role)) {
            return true;
          }
        }

        // All Investment Proposals (admin) — super user + HQ Management only
        if (permissionId === 'dashboardCards.allProposals') {
          const allowed = ['propcloudai_super_user', 'hq_manager', 'hq_admin'];
          if (allowed.includes(userProfile.role)) {
            return true;
          }
        }

        // If hierarchical permissions are configured but this card is not enabled, return false
        return false;
      }

      // If no hierarchical permissions are configured for this user, check if they should have default access based on role
      // This ensures new users with appropriate roles can see menus even without explicit hierarchical permissions

      // HQ Management card - restricted to management roles only, NOT consultants
      // IMPORTANT: This check MUST come before the generic 'dashboardCards.hq' check
      // because 'dashboardCards.hq_management' starts with 'dashboardCards.hq'
      if (permissionId === 'dashboardCards.hq_management') {
        const hqManagementRoles = ['propcloudai_super_user', 'hq_manager', 'hq_admin', 'hq_bonds'];
        if (hqManagementRoles.includes(userProfile.role)) {
          return true;
        }
        return false; // Explicitly deny for other roles including consultants
      }

      if (permissionId.startsWith('dashboardCards.hq')) {
        // HQ access for HQ-related roles
        const hqRoles = ['hq_manager', 'hq_admin', 'hq_bonds', 'hq_consultant_lvl1', 'hq_independent_consultant_lvl2', 'hq_referral_mandate', 'hq_advertising', 'hq_call_centre_agent'];
        if (hqRoles.includes(userProfile.role)) {
          return true;
        }
      }

      if (permissionId.startsWith('dashboardCards.partner')) {
        // Partner access - deprecated, only super user has access
        return false;
      }

      if (permissionId.startsWith('dashboardCards.crm')) {
        // CRM access for CRM and sales roles
        const crmRoles = ['hq_manager', 'hq_admin', 'hq_consultant_lvl1', 'hq_independent_consultant_lvl2'];
        if (crmRoles.includes(userProfile.role)) {
          return true;
        }
      }

      if (permissionId.startsWith('dashboardCards.documents') || permissionId.startsWith('dashboardCards.my_documents')) {
        // Documents access for all authenticated users
        return true;
      }

      if (permissionId.startsWith('dashboardCards.admin')) {
        // Admin access for admin roles
        const adminRoles = ['propcloudai_super_user', 'hq_manager', 'hq_admin'];
        if (adminRoles.includes(userProfile.role)) {
          return true;
        }
      }

      if (permissionId.startsWith('dashboardCards.advertising')) {
        // Advertising access
        const advertisingRoles = ['propcloudai_super_user', 'hq_manager', 'hq_admin', 'hq_advertising'];
        if (advertisingRoles.includes(userProfile.role)) {
          return true;
        }
      }

      if (permissionId.startsWith('dashboardCards.consultations')) {
        // Consultations access
        const consultationRoles = ['propcloudai_super_user', 'hq_manager', 'hq_admin', 'hq_consultant_lvl1', 'hq_independent_consultant_lvl2'];
        if (consultationRoles.includes(userProfile.role)) {
          return true;
        }
      }

      if (permissionId.startsWith('dashboardCards.leadStats')) {
        // Lead Stats access
        const leadStatsRoles = ['propcloudai_super_user', 'hq_manager', 'hq_admin', 'hq_consultant_lvl1', 'hq_independent_consultant_lvl2', 'hq_advertising'];
        if (leadStatsRoles.includes(userProfile.role)) {
          return true;
        }
      }

      if (permissionId.startsWith('dashboardCards.training')) {
        // Training access - available to all authenticated users
        return true;
      }

      if (permissionId.startsWith('dashboardCards.viewing')) {
        // Viewing Management access - include new viewing agent role
        const viewingRoles = ['propcloudai_super_user', 'hq_manager', 'hq_admin', 'hq_viewing_agent'];
        if (viewingRoles.includes(userProfile.role)) {
          return true;
        }
      }

      if (permissionId.startsWith('dashboardCards.viewingOversight')) {
        // Viewing Oversight access
        const viewingOversightRoles = ['propcloudai_super_user', 'hq_viewing_coordinator'];
        if (viewingOversightRoles.includes(userProfile.role)) {
          return true;
        }
      }

      if (permissionId === 'dashboardCards.investmentProposals') {
        const allowed = [
          'propcloudai_super_user',
          'hq_manager',
          'hq_admin',
          'hq_consultant_lvl1',
          'hq_independent_consultant_lvl2',
        ];
        if (allowed.includes(userProfile.role)) {
          return true;
        }
      }

      if (permissionId === 'dashboardCards.allProposals') {
        const allowed = ['propcloudai_super_user', 'hq_manager', 'hq_admin'];
        if (allowed.includes(userProfile.role)) {
          return true;
        }
      }

      // Default to false if no matching role
      return false;
    }

    // For regular permissions, check effective permissions if available
    if (effectivePermissions.length > 0) {
      return effectivePermissions.includes(permissionId);
    }

    // Fallback to checking permissions objects
    return checkHasPermission(permissions, permissionId);
  }, [permissions, userProfile?.role, hierarchicalPermissions, effectivePermissions]);

  const hasPermissions = useCallback((permissionIds: string[]): boolean => {
    if (!userProfile?.role) return false;

    // Super users always have all permissions
    if (userProfile.role === UserRole.PROPCLOUDAI_SUPER_USER) {
      return true;
    }

    // Use effective permissions if available
    if (effectivePermissions.length > 0) {
      return permissionIds.every(id => effectivePermissions.includes(id));
    }

    // Fallback to permission objects
    return checkHasAllPermissions(permissions, permissionIds);
  }, [permissions, userProfile?.role, effectivePermissions]);

  const hasAnyPermission = useCallback((permissionIds: string[]): boolean => {
    if (!userProfile?.role) return false;

    // Super users always have all permissions
    if (userProfile.role === UserRole.PROPCLOUDAI_SUPER_USER) {
      return true;
    }

    // Use effective permissions if available
    if (effectivePermissions.length > 0) {
      return permissionIds.some(id => effectivePermissions.includes(id));
    }

    // Fallback to permission objects
    return checkHasAnyPermission(permissions, permissionIds);
  }, [permissions, userProfile?.role, effectivePermissions]);

  const checkPermissionAsync = useCallback(async (permissionId: string): Promise<PermissionCheckResult> => {
    if (!userProfile?.role) {
      return {
        hasPermission: false,
        reason: 'User not authenticated'
      };
    }
    
    // Check if it's a hierarchical permission
    if (permissionId.startsWith('dashboardCards.')) {
      const hasAccess = await checkHierarchicalPermission(userProfile.role as UserRole, permissionId);
      return {
        hasPermission: hasAccess,
        reason: hasAccess ? undefined : 'Hierarchical permission not granted'
      };
    }
    
    return checkPermission(userProfile.role as UserRole, permissionId);
  }, [userProfile?.role]);

  const refreshPermissions = useCallback(async () => {
    await loadPermissions();
  }, [loadPermissions]);

  // Storage permission helpers
  const canUploadTo = useCallback((path: string): boolean => {
    if (!userProfile?.role) return false;
    return canWriteToPathSync(userProfile.role as UserRole, path, storagePermissions);
  }, [userProfile?.role, storagePermissions]);

  const canDownloadFrom = useCallback((path: string): boolean => {
    if (!userProfile?.role) return false;
    return canReadFromPathSync(userProfile.role as UserRole, path, storagePermissions);
  }, [userProfile?.role, storagePermissions]);

  const value: PermissionContextType = {
    permissions,
    loading,
    hasPermission,
    hasPermissions,
    hasAnyPermission,
    checkPermissionAsync,
    refreshPermissions,
    hasHierarchicalPermissions: !!(
      // Has explicit hierarchical permissions
      (hierarchicalPermissions && hierarchicalPermissions.dashboardCards) ||
      // Or is a super user
      userProfile?.role === UserRole.PROPCLOUDAI_SUPER_USER ||
      // Or has a role that should have default access
      (userProfile?.role && (
        ['hq_manager', 'hq_admin', 'hq_bonds', 'hq_consultant_lvl1', 'hq_independent_consultant_lvl2', 'hq_referral_mandate', 'hq_advertising', 'hq_viewing_agent', 'hq_viewing_coordinator', 'hq_call_centre_agent', 'ai_agent_lvl1'].includes(userProfile.role)
      ))
    ),
    effectivePermissions,
    hasCustomPermissions,
    storagePermissions,
    canUploadTo,
    canDownloadFrom
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;